import React, { useEffect, useContext, useState, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { BASE_URL } from "../../../../../BaseUrl";
import {
  _t,
  getCookie,
  tableLoading,
  formatNumber,
  formatPrice,
  customStyle,
} from "../../../../../functions/Functions";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";
import ManageSidebar from "../ManageSidebar";
import NumberFormatter from "../../../component/NumberFormatter";
import { RestaurantContext } from "../../../../../contexts/Restaurant";

import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";

const LIST_PATH = "/dashboard/manage/stock/bank-drop";

const formatDocDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const parseAmount = (value) => {
  const n = parseFloat(value);
  return Number.isNaN(n) || value === "" ? 0 : n;
};

const BankDropForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    //branch
    branchForSearch,
    //md accounts
    loading,
    setLoading,
  } = useContext(RestaurantContext);

  const [bankDrop, setBankDrop] = useState({
    doc_date: new Date(),
    branch: "",
    remarks: "",
    bank_account_id: "",
    bank_amount: "",
    expense_account_id: "",
    expense_amount: "",
    trapped_account_id: "",
    trapped_amount: "",
  });

  const fetchedForIdRef = useRef(null);
  const [fetchingAccDetail, setFetchingAccDetail] = useState(false);

  const fetchBankDrop = useCallback(async () => {

    setLoading(true);

    try {

      const { data } = await axios.get(`${BASE_URL}/settings/bank-drop/${id}`, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      if (!data) return;

      setBankDrop({
        doc_date: data.document_date ? new Date(data.document_date) : new Date(),
        branch: branchForSearch?.find((b) => b.id === data.branch_id) ?? "",
        remarks: data.remarks ?? "",
        bank_account_id: data.bank_account_id,
        expense_account_id: data.expense_account_id,
        trapped_account_id: data.trapped_account_id,
        trapped_amount: parseAmount(data.trapped_amount),
        bank_amount: parseAmount(data.bank_amount),
        expense_amount: parseAmount(data.expense_amount),
      });


    } catch {
      ToastError({ title: t("Please try again later") });
    } finally {
      setLoading(false);
    }
  }, [id, branchForSearch]);

  const fetchAccDetail = async (branch_id) => {
    if (!branch_id) return;
    setFetchingAccDetail(true);
    const url = `${BASE_URL}/settings/get-bank-drop/${branch_id}`;
    try {
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (!data) return;
      setBankDrop((prev) => ({
        ...prev,
        bank_account_id: data.bank_drop_account,
        expense_account_id: data.expense_account ,
        trapped_account_id: data.trapped_account,
      }));
    } catch (err) {
      const message = err?.response?.data?.message||err?.response?.message || t("Please try again later");
      ToastError({ title: message });
    } finally {
      setFetchingAccDetail(false);
    }
  };

  useEffect(() => {
    if (!isEdit || !id || !branchForSearch) return;
    if (fetchedForIdRef.current === id) return;
    fetchedForIdRef.current = id;
    fetchBankDrop();
  }, [isEdit, id, branchForSearch, fetchBankDrop]);



  const saveAccount = async (formData) => {

    setLoading(true);

    const url = `${BASE_URL}/settings/bank-drop${isEdit ? `/${id}` : ""}`;
    const method = isEdit ? "put" : "post";
    try {
      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      ToastSuccess({
        title: t(isEdit ? "Bank drop has been updated" : "Bank drop has been added"),
      });
      history.push(LIST_PATH);
    } catch {
      ToastError({ title: t("Please try again later") });
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = useCallback(() => {
    return parseAmount(bankDrop.bank_amount) + parseAmount(bankDrop.expense_amount);
  }, [bankDrop.bank_amount, bankDrop.expense_amount]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!bankDrop.branch) {
      ToastError({ title: t("Please select branch") });
      return;
    }

    const total = totalAmount();
    if (total <= 0) {
      ToastError({ title: t("Please enter amount") });
      return;
    }

    if (!bankDrop.bank_account_id || !bankDrop.expense_account_id) {
      ToastError({ title: t("Please set bank and expense accounts!") });
      return;
    }

    if (!bankDrop.trapped_account_id) {
      ToastError({ title: t("Please set trapped account!") });
      return;
    }

    const payload = {
      document_date: formatDocDate(bankDrop.doc_date),
      remarks: bankDrop.remarks,
      branch_id: bankDrop.branch.id,
      bank_account_id: bankDrop.bank_account_id,
      expense_account_id: bankDrop.expense_account_id,
      trapped_account_id: bankDrop.trapped_account_id,
      bank_amount: parseAmount(bankDrop.bank_amount),
      expense_amount: parseAmount(bankDrop.expense_amount),
      trapped_amount: parseAmount(total),
    };
    saveAccount(payload);
  };

  const updateBankDrop = (updates) => {
    fetchAccDetail(updates?.branch?.id);
    setBankDrop((prev) => ({ ...prev, ...updates }));
  };


  const blockInvalidKeys = (evt) => {
    if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(evt.key)) {
      evt.preventDefault();
    }
  };

  const pageTitle = _t(t(isEdit ? "Edit Bank Drop" : "Add Bank Drop"));

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0" key="bank-drop-form" style={{ position: "relative" }}>
              {fetchingAccDetail && (
                <div>
                  <CustomSpinLoader color="primary" loading={fetchingAccDetail} />
                </div>
              )}
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    {loading ? (
                      tableLoading()
                    ) : (
                      < >

                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {pageTitle}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center" />
                          </div>
                        </div>

                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              <div className="form-group col-2 mt-2">
                                <div className="mb-2">
                                  <label htmlFor="invoice" className="control-label">
                                    {_t(t("Document #"))}
                                  </label>
                                </div>
                                <input
                                  disabled
                                  type="text"
                                  className="form-control table-text"
                                  id="invoice"
                                  placeholder="Auto No"
                                />
                              </div>

                              <div className="form-group col-2 text-right mt-2">
                                <div className="mb-2 text-left">
                                  <label htmlFor="purchaseDate" className="control-label">
                                    {_t(t("Document Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <DatePicker
                                  selected={bankDrop.doc_date}
                                  onChange={(date) => updateBankDrop({ doc_date: date })}
                                  peekNextMonth
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                  className="form-control table-text"
                                  placeholderText={_t(t("Document date"))}
                                  shouldCloseOnSelect={false}
                                  required
                                  disabled
                                />
                              </div>

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2 text-left">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={branchForSearch ?? []}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={bankDrop.branch || ""}
                                  classNamePrefix="select table-text"
                                  onChange={(branch) => updateBankDrop({ branch })}
                                  placeholder={`${_t(t("Please select branch"))}..`}
                                  styles={customStyle}
                                  required
                                />
                              </div>

                              <div className="form-group mt-2 col-5">
                                <div className="mb-2">
                                  <label htmlFor="description" className="control-label">
                                    {_t(t("Description"))}
                                  </label>
                                </div>
                                <input
                                  type="text"
                                  className="form-control table-text"
                                  id="description"
                                  onChange={(e) => updateBankDrop({ remarks: e.target.value })}
                                  value={bankDrop.remarks}
                                  placeholder="Short description"
                                />
                              </div>
                            </div>

                            <div
                              className="table-responsive pt-3 mt-2 pr-1"
                              style={{ maxHeight: "21rem", overflow: "auto" }}
                            >
                              <table className="table table-bordered-sm table-hover">
                                <thead className="align-middle">
                                  <tr>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border table-Child-bank"
                                    >
                                      <span style={{ fontWeight: 500 }}>{_t(t("Type"))}</span>
                                    </th>

                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>{_t(t("Amount"))}</span>
                                    </th>
                                  </tr>
                                </thead>

                                <tbody className="align-middle">
                                  <tr className="align-middle">
                                    <td
                                      className="table-text text-capitalize align-middle text-center table-firstChild"
                                      style={{ minWidth: "60px" }}
                                    >
                                      Bank Drop
                                    </td>
                                    <td
                                      className="table-text text-capitalize align-middle text-center"
                                      style={{ minWidth: "140px" }}
                                    >
                                      <NumberFormatter>
                                        <input
                                          type="text"
                                          step="0.01"
                                          min="0"
                                          onKeyDown={blockInvalidKeys}
                                          onWheel={(e) => e.target.blur()}
                                          className="form-control text-center table-Input table-text"
                                          onChange={(e) => updateBankDrop({ bank_amount: e.target.value })}
                                          placeholder="0"
                                          required
                                          value={bankDrop.bank_amount}
                                        />
                                      </NumberFormatter>
                                    </td>
                                  </tr>
                                  <tr className="align-middle">
                                    <td
                                      className="table-text text-capitalize align-middle text-center table-firstChild"
                                      style={{ minWidth: "60px" }}
                                    >
                                      Expense
                                    </td>
                                    <td
                                      className="table-text text-capitalize align-middle text-center"
                                      style={{ minWidth: "140px" }}
                                    >
                                      <NumberFormatter>
                                        <input
                                          type="text"
                                          step="0.01"
                                          min="0"
                                          onKeyDown={blockInvalidKeys}
                                          onWheel={(e) => e.target.blur()}
                                          className="form-control text-center table-Input table-text"
                                          onChange={(e) => updateBankDrop({ expense_amount: e.target.value })}
                                          placeholder="0"
                                          required
                                          value={bankDrop.expense_amount}
                                        />
                                      </NumberFormatter>
                                    </td>
                                  </tr>
                                </tbody>

                                <tfoot className="align-middle">
                                  <tr>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-right border-1 border font-weight-bold"
                                    >
                                      {_t(t("Total Amount"))}
                                    </th>
                                    <th
                                      scope="col"
                                      style={{ minWidth: "100px" }}
                                      className="table-text text-capitalize align-middle text-center border-1 border font-weight-bold"
                                    >
                                      <span style={{ fontWeight: "bold" }}>
                                        {formatPrice(totalAmount())}
                                      </span>
                                    </th>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>

                            <div className="form-group mt-4 pb-2">
                              <div className="col-12 text-right">
                                <button
                                  type="button"
                                  className="btn hov-btn px-4 mr-3 rounded"
                                  onClick={() => history.push(LIST_PATH)}
                                >
                                  <i className="fa fa-arrow-left" aria-hidden="true" />{" "}
                                  {_t(t("Back"))}
                                </button>
                                <button type="submit" className="btn btn-primary px-4 rounded">
                                  <i className="fa fa-floppy-o" aria-hidden="true" />{" "}
                                  {isEdit ? _t(t("Update")) : _t(t("Save"))}
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default BankDropForm;
